import { useEffect, useMemo, useRef, useState } from 'react';
import { GLOSSARY } from '../content/glossary';
import { useApp } from '../state/store';
import { renderMath } from './MathTex';
import { TermTipContent, useTermPopover } from './Term';

interface TipState {
  id: string;
  top: number;
  left: number;
}

/**
 * Display math whose symbols are live glossary terms. Symbols wrapped in
 * `\term{id}{…}` render with a `data-term` attribute; here we make those
 * spans show a hover tooltip, highlight the matching force/signal, and open
 * the shared glossary popover on click. KaTeX puts these spans in the
 * aria-hidden visual layer, so the reading for assistive tech comes from the
 * MathML layer by default, or from the plain `aria` string when provided.
 */
export function InteractiveMath({ tex, aria }: { tex: string; aria?: string }) {
  const html = useMemo(() => renderMath(tex, true, aria ? 'html' : 'htmlAndMathml'), [tex, aria]);
  const rootRef = useRef<HTMLSpanElement>(null);
  const open = useTermPopover((s) => s.open);
  const setHighlight = useApp((s) => s.setHighlight);
  const [tip, setTip] = useState<TipState | null>(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const nodes = Array.from(root.querySelectorAll<HTMLElement>('[data-term]'));
    const disposers: Array<() => void> = [];
    for (const node of nodes) {
      const id = node.dataset.term ?? '';
      const entry = GLOSSARY[id];
      if (!entry) continue;
      node.classList.add('math-term');
      // Focusable only programmatically (e.g. popover return-focus), never in
      // the tab order, since it lives inside the aria-hidden visual layer.
      node.setAttribute('tabindex', '-1');
      const showTip = () => {
        if (entry.highlight && !useApp.getState().highlightSticky) setHighlight(entry.highlight);
        const nodeRect = node.getBoundingClientRect();
        const rootRect = root.getBoundingClientRect();
        setTip({ id, top: nodeRect.bottom - rootRect.top + 4, left: nodeRect.left - rootRect.left });
      };
      const hideTip = () => setTip((t) => (t?.id === id ? null : t));
      const onClick = () => {
        if (entry.highlight && !useApp.getState().highlightSticky) setHighlight(entry.highlight);
        open(id, node);
      };
      node.addEventListener('pointerenter', showTip);
      node.addEventListener('pointerleave', hideTip);
      node.addEventListener('click', onClick);
      disposers.push(() => {
        node.removeEventListener('pointerenter', showTip);
        node.removeEventListener('pointerleave', hideTip);
        node.removeEventListener('click', onClick);
      });
    }
    return () => disposers.forEach((d) => d());
  }, [html, open, setHighlight]);

  const tipEntry = tip ? GLOSSARY[tip.id] : null;
  return (
    <span className="math-interactive" ref={rootRef}>
      {aria && <span className="sr-only">{aria}</span>}
      <span className="math-block" aria-hidden={aria ? true : undefined} dangerouslySetInnerHTML={{ __html: html }} />
      {tip && tipEntry && (
        <span className="math-tip" role="tooltip" style={{ top: tip.top, left: tip.left }}>
          <TermTipContent id={tip.id} />
        </span>
      )}
    </span>
  );
}
